import styled from '@emotion/styled';

import type { PasswordLevel } from '@/features/auth/password-strength';
import { Typography } from '@/shared/design-system';

type PasswordLevelIndicatorProps = {
  level: PasswordLevel;
};

const IndicatorRoot = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing[8],
}));

const Bars = styled('div')(({ theme }) => ({
  width: '100%',
  display: 'flex',
  gap: theme.spacing[8],
}));

const LevelBar = styled('span')<{ $filled: boolean; $level: PasswordLevel }>(({
  theme,
  $filled,
  $level,
}) => {
  const colors = {
    Weak: theme.colors.error.solid,
    Medium: theme.colors.warning.solid,
    Strong: theme.colors.brand.solid,
  };
  return {
    height: 5,
    flex: 1,
    borderRadius: theme.radius[8],
    backgroundColor: $filled ? colors[$level] : theme.colors.border.default,
  };
});

const LevelText = styled(Typography)(({ theme }) => ({
  color: theme.colors.text.primary,
}));

export const PasswordLevelIndicator = ({ level }: PasswordLevelIndicatorProps) => {
  const filledBars = { Weak: 1, Medium: 2, Strong: 3 }[level];

  return (
    <IndicatorRoot aria-live="polite">
      <Bars aria-hidden="true">
        {Array.from({ length: 3 }, (_, index) => (
          <LevelBar key={index} $filled={index < filledBars} $level={level} />
        ))}
      </Bars>
      <LevelText as="span" variant="body3">
        {level === 'Weak' ? 'Low' : level}
      </LevelText>
    </IndicatorRoot>
  );
};
